/**
 * CRM Multi-Compagnia · Dashboard - FiltersBar
 * Barra filtri unificata: compagnia, ramo, subagente, date range, stato polizza, stato incasso.
 * Persistenza in URL querystring + debounced onChange.
 */
import { useEffect, useMemo, useState } from "react";
import { RotateCcw, Download, Filter as FilterIcon } from "lucide-react";
import { api, useApi, Btn, RAMI_LABEL } from "@/components/crm-multi/shared";

const STATI_POLIZZA = ["bozza", "attiva", "sospesa", "scaduta", "disdetta", "annullata"];
const STATI_INCASSO = ["da_incassare", "incassata", "insoluta", "rimborsata"];

function readQS() {
  const sp = new URLSearchParams(window.location.search);
  return {
    company_id: sp.get("company_id") || "",
    ramo: sp.get("ramo") || "",
    subagent_id: sp.get("subagent_id") || "",
    periodo_da: sp.get("periodo_da") || "",
    periodo_a: sp.get("periodo_a") || "",
    stato_polizza: sp.get("stato_polizza") || "",
    stato_incasso: sp.get("stato_incasso") || "",
  };
}

function writeQS(filters) {
  const sp = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v) sp.set(k, v); });
  const qs = sp.toString();
  const url = window.location.pathname + (qs ? "?" + qs : "");
  window.history.replaceState({}, "", url);
}

export default function FiltersBar({ value, onChange, onExportCsv }) {
  const compagnie = useApi(() => api.get("/companies", { attiva: true, limit: 200 }));
  const subagenti = useApi(() => api.get("/subagents", { attivo: true }));
  const rami = useApi(() => api.get("/meta/rami"));

  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);

  // Debounce 300ms
  useEffect(() => {
    const t = setTimeout(() => {
      const dirty = Object.keys(local).some(k => (local[k] || "") !== (value[k] || ""));
      if (dirty) {
        writeQS(local);
        onChange(local);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  const setField = (k, v) => setLocal({ ...local, [k]: v });
  const reset = () => {
    const empty = readQS();
    Object.keys(empty).forEach(k => empty[k] = "");
    setLocal(empty);
    writeQS(empty);
    onChange(empty);
  };

  const activeCount = useMemo(() => Object.values(local).filter(Boolean).length, [local]);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-3" data-testid="cm-dash-filters">
      <div className="flex items-center gap-2 mb-3">
        <FilterIcon className="w-4 h-4 text-zinc-500" />
        <h3 className="text-sm font-semibold text-zinc-900">Filtri</h3>
        {activeCount > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-medium" data-testid="cm-dash-filters-count">
            {activeCount} attivi
          </span>
        )}
        <div className="ml-auto flex gap-2">
          {onExportCsv && (
            <Btn variant="outline" size="sm" onClick={onExportCsv} data-testid="cm-dash-export">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </Btn>
          )}
          <Btn variant="ghost" size="sm" onClick={reset} data-testid="cm-dash-reset">
            <RotateCcw className="w-3.5 h-3.5" /> Reset
          </Btn>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        <select value={local.company_id} onChange={(e) => setField("company_id", e.target.value)}
          className="px-2 py-1.5 text-xs border border-zinc-300 rounded bg-white" data-testid="cm-dash-filter-company">
          <option value="">Tutte compagnie</option>
          {(compagnie.data?.items || []).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <select value={local.ramo} onChange={(e) => setField("ramo", e.target.value)}
          className="px-2 py-1.5 text-xs border border-zinc-300 rounded bg-white" data-testid="cm-dash-filter-ramo">
          <option value="">Tutti rami</option>
          {(rami.data?.items || []).map(r => <option key={r.code} value={r.code}>{r.label || RAMI_LABEL[r.code] || r.code}</option>)}
        </select>
        <select value={local.subagent_id} onChange={(e) => setField("subagent_id", e.target.value)}
          className="px-2 py-1.5 text-xs border border-zinc-300 rounded bg-white" data-testid="cm-dash-filter-subagent">
          <option value="">Tutti subagenti</option>
          {(subagenti.data?.items || []).map(s => <option key={s.id} value={s.id}>{s.nome} {s.cognome}</option>)}
        </select>
        <input type="date" value={local.periodo_da} onChange={(e) => setField("periodo_da", e.target.value)}
          className="px-2 py-1.5 text-xs border border-zinc-300 rounded" placeholder="da" data-testid="cm-dash-filter-da" />
        <input type="date" value={local.periodo_a} onChange={(e) => setField("periodo_a", e.target.value)}
          className="px-2 py-1.5 text-xs border border-zinc-300 rounded" placeholder="a" data-testid="cm-dash-filter-a" />
        <select value={local.stato_polizza} onChange={(e) => setField("stato_polizza", e.target.value)}
          className="px-2 py-1.5 text-xs border border-zinc-300 rounded bg-white" data-testid="cm-dash-filter-statopol">
          <option value="">Tutti stati polizza</option>
          {STATI_POLIZZA.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={local.stato_incasso} onChange={(e) => setField("stato_incasso", e.target.value)}
          className="px-2 py-1.5 text-xs border border-zinc-300 rounded bg-white" data-testid="cm-dash-filter-statoinc">
          <option value="">Tutti stati incasso</option>
          {STATI_INCASSO.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
    </div>
  );
}

export { readQS };
